import { useEffect, useState } from 'react';
import { useBalanceStore } from '../stores/balanceStore';
import { useAuthStore } from '../stores/authStore';
import { useAccountsStore } from '../stores/accountsStore';
import { useAppSettingsStore } from '../stores/appSettingsStore';
import { logWithTimestamp } from '../utils/dateHelpers';
import type { AccountData } from '@buzunarul/shared-constants';

/**
 * Hook pentru inițializarea balanceStore la app startup
 * 
 * Gestionează:
 * - Detectarea hidratării complete a store-urilor
 * - Recalcularea dailyBalances din tranzacții
 * - Loading state pentru perioada de inițializare
 * - Sincronizarea cu accountSettings pentru balance calculation
 */
export const useBalanceStoreInitialization = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  
  // Store references
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { hasBalanceStartDate, getBalanceStartDate } = useAppSettingsStore();
  const { calculateDailyBalances, dailyBalances } = useBalanceStore();

  // 🔄 MIGRATION HELPER - Migrează de la settingsStore la noile stores separate
  const performMigrationIfNeeded = async (): Promise<boolean> => {
    try {
      const oldSettingsData = localStorage.getItem('budget-app-settings');
      if (!oldSettingsData) {
        return false; // No old data to migrate
      }

      let parsedData;
      try {
        parsedData = JSON.parse(oldSettingsData);
      } catch (error) {
        logWithTimestamp('[Migration] Invalid JSON in old settingsStore, cleaning up...');
        localStorage.removeItem('budget-app-settings');
        return false;
      }

      const oldState = parsedData?.state;
      if (!oldState) {
        logWithTimestamp('[Migration] No state in old settingsStore');
        localStorage.removeItem('budget-app-settings');
        return false;
      }

      logWithTimestamp('[Migration] Found old settingsStore data, migrating...');

      // Check if new stores are already populated (skip migration)
      const currentAccounts = useAccountsStore.getState().accounts;
      const currentAppSettings = useAppSettingsStore.getState().appSettings;
      
      if (currentAccounts.length > 0 && currentAppSettings.defaultCurrency) {
        logWithTimestamp('[Migration] New stores already populated, skipping migration');
        return false;
      }

      // Migrate accounts if they exist
      if (oldState.accounts && Array.isArray(oldState.accounts) && oldState.accounts.length > 0) {
        logWithTimestamp(`[Migration] Migrating ${oldState.accounts.length} accounts...`);
        useAccountsStore.setState({
          accounts: oldState.accounts,
          defaultAccountId: oldState.defaultAccountId || null,
        });
      }

      // Migrate app settings if they exist  
      if (oldState.appSettings && typeof oldState.appSettings === 'object') {
        logWithTimestamp('[Migration] Migrating app settings...');
        
        // Map old structure to new structure
        const migratedSettings = {
          defaultCurrency: oldState.appSettings.defaultCurrency || 'RON',
          dateFormat: oldState.appSettings.dateFormat || 'DD/MM/YYYY',
          enableMultiAccount: oldState.appSettings.enableMultiAccount || false,
          enableNotifications: oldState.appSettings.enableNotifications !== false,
          themeMode: (oldState.appSettings.darkMode ? 'dark' : 'light') as 'light' | 'dark' | 'system',
          autoSaveInterval: oldState.appSettings.autoSaveInterval || 30000,
          balanceStartDate: oldState.appSettings.balanceStartDate || new Date().toISOString().split('T')[0],
          enableMonthlyTransfers: oldState.appSettings.enableMonthlyTransfers || false,
          compactMode: false,
          showBalanceInTitle: false,
          confirmDeleteActions: true,
        };

        useAppSettingsStore.setState({
          appSettings: migratedSettings,
        });
      }

      // Clean up old data after successful migration
      localStorage.removeItem('budget-app-settings');
      logWithTimestamp('[Migration] Migration completed successfully, old data cleaned up');
      
      return true;
    } catch (error) {
      logWithTimestamp(`[Migration] Migration error: ${error}`);
      return false;
    }
  };

  // Track hydration completion
  useEffect(() => {
    const initializeBalanceStore = async () => {
      try {
        logWithTimestamp('[BalanceStore] Starting initialization...');
        setIsInitializing(true);
        setInitializationError(null);

        // 🔄 MIGRATION LOGIC - Check for old settingsStore data first
        const migrationPerformed = await performMigrationIfNeeded();
        if (migrationPerformed) {
          logWithTimestamp('[BalanceStore] Migration completed, waiting for stores to update...');
          return; // Let the stores rehydrate and try again
        }

        // Wait for authentication to be ready
        if (!user) {
          logWithTimestamp('[BalanceStore] Waiting for user authentication...');
          return;
        }

        // Wait for account settings to be hydrated
        if (!accounts || accounts.length === 0) {
          logWithTimestamp('[BalanceStore] Waiting for account settings...');
          return;
        }

        // For now, we'll trigger recalculation with empty transactions array
        // TODO: In a full implementation, we would fetch actual transactions here
        // and calculate dailyBalances based on real data
        
        logWithTimestamp('[BalanceStore] Recalculating dailyBalances...');
        
        // Calculate initial balance from accounts
        const initialBalance = accounts
          .filter((acc: AccountData) => acc.isActive && acc.includedInTotal !== false)
          .reduce((sum: number, acc: AccountData) => sum + (acc.currentBalance ?? acc.initialBalance ?? 0), 0);

        // Get start date for calculations
        const startDate = hasBalanceStartDate() 
          ? getBalanceStartDate() || new Date().toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];

        // For this implementation, we'll use empty transactions array
        // In production, this should fetch transactions from the service
        const transactions: unknown[] = [];
        
        calculateDailyBalances(transactions, initialBalance, startDate);
        
        logWithTimestamp('[BalanceStore] Initialization completed successfully');
        setIsInitializing(false);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error during initialization';
        logWithTimestamp(`[BalanceStore] Initialization error: ${errorMessage}`);
        setInitializationError(errorMessage);
        setIsInitializing(false);
      }
    };

    // Run initialization after a short delay to ensure stores are hydrated
    const initTimeout = setTimeout(initializeBalanceStore, 100);
    
    return () => clearTimeout(initTimeout);
  }, [user, accounts, calculateDailyBalances, hasBalanceStartDate]);

  return {
    isInitializing,
    initializationError,
    isInitialized: !isInitializing && !initializationError,
    dailyBalancesCount: dailyBalances.length
  };
}; 